import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, Bell } from "lucide-react";
import { toast } from "sonner";

const EVENTS = [
  { id: "shift_assigned",  label: "New shift assigned to me", hint: "Every time a manager puts a shift on your schedule." },
  { id: "request_decided", label: "My request was decided",   hint: "PTO, trade, pickup, and shift-modification decisions." },
  { id: "open_shift",      label: "An open shift is available", hint: "Grab it before someone else does." },
  { id: "manager_pending", label: "New request needs my approval", hint: "Managers only — quiet for regular employees." },
];

/**
 * Preferences matrix.  Two flavours:
 * - `userId=null` → the current signed-in user (uses /me/notification-prefs)
 * - `userId=<uuid>` → manager override (uses /users/{id}/notification-prefs)
 */
export default function NotificationPrefs({ userId = null, showManagerHint = false, compact = false }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const endpoint = userId ? `/users/${userId}/notification-prefs` : "/me/notification-prefs";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(endpoint);
        if (alive) setPrefs(data.prefs || {});
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [endpoint]);

  const flip = (ev, ch) => {
    setPrefs(p => ({
      ...p,
      [ev]: { ...(p?.[ev] || {}), [ch]: !(p?.[ev]?.[ch]) },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, { prefs });
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    }
    setSaving(false);
  };

  if (!prefs) return <div className="text-xs text-slate-500">Loading preferences…</div>;

  return (
    <div className="space-y-2" data-testid="notification-prefs">
      {showManagerHint && (
        <div className="text-[11px] text-slate-500">
          Employees can adjust these themselves from "My Info". Any changes here override their choices.
        </div>
      )}
      <div className={`grid ${compact ? "grid-cols-[1fr_auto_auto]" : "grid-cols-[1fr_80px_80px]"} gap-2 items-center text-xs font-semibold text-slate-500 uppercase tracking-wider pb-1 border-b border-slate-100`}>
        <div>Event</div>
        <div className="text-center inline-flex items-center gap-1 justify-center"><Bell size={11} /> In-app</div>
        <div className="text-center inline-flex items-center gap-1 justify-center"><Mail size={11} /> Email</div>
      </div>
      {EVENTS.map(ev => (
        <div key={ev.id} className={`grid ${compact ? "grid-cols-[1fr_auto_auto]" : "grid-cols-[1fr_80px_80px]"} gap-2 items-center py-1.5`} data-testid={`pref-row-${ev.id}`}>
          <div>
            <div className="text-sm font-medium">{ev.label}</div>
            {!compact && <div className="text-[11px] text-slate-500">{ev.hint}</div>}
          </div>
          <div className="text-center">
            <Switch
              checked={!!(prefs[ev.id]?.in_app ?? true)}
              onCheckedChange={() => flip(ev.id, "in_app")}
              data-testid={`pref-${ev.id}-in_app`}
            />
          </div>
          <div className="text-center">
            <Switch
              checked={!!(prefs[ev.id]?.email ?? true)}
              onCheckedChange={() => flip(ev.id, "email")}
              data-testid={`pref-${ev.id}-email`}
            />
          </div>
        </div>
      ))}
      <div className="pt-2">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid="pref-save">
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}
